/**
 * DashboardSidebar - Sidebar za Dashboard layout sa Dashboard dizajnom
 * 
 * Glassmorphism sidebar sa navigacijom, profilom i logout opcijom.
 * Podržava collapse/expand i mobile drawer.
 * 
 * @example
 * ```tsx
 * <DashboardSidebar isCollapsed={false} onCollapse={handleCollapse} />
 * <DashboardSidebar isMobile onClose={handleClose} />
 * ```
 */

"use client";

import { Box, Flex, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { TouchAwareTooltip } from "@/components/ui";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineChevronLeft,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { Link, ConfirmationModal, LOGOUT_CONFIRMATION_LOADING } from "@/components";
import { LogoutTransitionOverlay } from "@/components/auth/LogoutTransitionOverlay";
import {
  getPortalLinks,
  getAdminLinkGroups,
  getChoreographerLinkGroups,
  getStaffLinkGroups,
  getTeacherLinkGroups,
  type StudioNavOptions,
} from "@/components/shared/sidebar/linkItems";
import { useStudioFeatureSettings } from "@/features/dashboard/hooks/useStudioFeatureSettings";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { useAuth } from "@/lib/contexts";
import { resolveVisibleActiveRole } from "@/lib/utils/roles";
import { useThemeTokens } from "@/lib/theme/tokens";
import { PageTypePortal, PageTypeAdmin } from "@/lib/types";
import { useTenantData, useLogout } from "@/hooks";
import { useMediaAssetGet } from "@/features/media/hooks";
import { getTenantDisplayName } from "@/lib/utils/tenant";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { SidebarThemeToggle } from "@/components/shared/sidebar/SidebarThemeToggle";
import { getSidebarLogoutButtonStyles } from "@/components/shared/sidebar/sidebarLogoutButtonStyles";
import { SidebarNavCollapsibleGroup } from "@/components/shared/sidebar/SidebarNavCollapsibleGroup";
import { SidebarNavLinkItem } from "@/components/shared/sidebar/SidebarNavLinkItem";
import { useSidebarGroupExpansion } from "@/components/shared/sidebar/useSidebarGroupExpansion";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
  sidebarNavRowAlign,
} from "@/components/shared/sidebar/SidebarNavItemLabel";
import {
  useMemberMakeupsNeedingActionCount,
  usePendingMakeupRequestsCount,
} from "@/features/makeups/hooks";
import { canReviewMakeupRequests } from "@/lib/permissions";
import { toMakeupMemberId } from "@/lib/services/makeupService";
import type { MakeupNavBadge } from "@/components/shared/sidebar/makeupNavBadge";
import type { UserTasksNavBadge } from "@/components/shared/sidebar/userTasksNavBadge";
import { useActiveUserTasksCount } from "@/features/user-tasks/hooks";
import { UserRoles } from "@/lib/types/user-roles";
import { hexToRgba } from "@/lib/utils/color";
import { fullViewportHeightProps } from "@/lib/layout/viewport";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import { StudioTimeZoneHint } from "@/components/shared/studio-time";

interface DashboardSidebarProps {
  isCollapsed?: boolean;
  onCollapse?: () => void;
  onClose?: () => void;
  isMobile?: boolean;
  initialLogoUrl?: string | null;
  initialTenantName?: string | null;
}

export const DashboardSidebar = ({
  isCollapsed = false,
  onCollapse,
  onClose,
  isMobile = false,
  initialLogoUrl,
  initialTenantName,
}: DashboardSidebarProps) => {
  const user = useAuth();
  const pathname = usePathname();
  const isAdminPage = pathname?.startsWith("/admin") ?? false;
  const theme = useThemeTokens();
  const { isClassic, controlBorderRadius, backdropBlur } =
    useClassicDashboardStyles();
  const { tenantData, tenantId } = useTenantData();
  const [showStudioTimeZoneHint, setShowStudioTimeZoneHint] = useState(false);
  const {
    isLogoutModalOpen,
    onLogoutModalOpen,
    onLogoutModalClose,
    handleLogout,
    isLoggingOut,
    showLogoutOverlay,
    logoUrl: logoutLogoUrl,
    tenantData: logoutTenantData,
  } = useLogout({
    callbackUrl: isAdminPage ? PageTypeAdmin.SignIn : PageTypePortal.Login,
  });

  const tenantName = getTenantDisplayName(tenantData) ?? initialTenantName;

  const mediaAssetParams = useMemo(
    () => ({
      tenantId,
      ownerType: EntryType.Tenant,
      ownerId: tenantId,
      mediaGroup: MediaGroup.Logo,
    }),
    [tenantId],
  );

  const { url: logoUrl } = useMediaAssetGet(
    mediaAssetParams,
    { enabled: !!tenantId && tenantId > 0 },
  );

  const isForbiddenPage = pathname === "/forbidden";
  const canShowAdminMakeupBadge =
    isAdminPage && canReviewMakeupRequests(user);
  const { count: pendingMakeupRequestsCount } = usePendingMakeupRequestsCount(
    canShowAdminMakeupBadge,
  );
  const memberId = toMakeupMemberId(user?.id);
  const {
    makeupsEnabled,
    appointmentsEnabled,
    timeClockEnabled,
    choreographerPortalEnabled,
    contractorsEnabled,
    ticketsEnabled,
    isPending: isFeatureSettingsPending,
  } = useStudioFeatureSettings();
  const portalKnown = !isFeatureSettingsPending;
  const visibleRole = resolveVisibleActiveRole(
    user,
    choreographerPortalEnabled,
    portalKnown,
  );
  const isFullAdmin = isAdminPage && visibleRole === UserRoles.Admin;
  const { count: memberMakeupActionCount } = useMemberMakeupsNeedingActionCount(
    !isAdminPage && !!user?.isMember && makeupsEnabled,
    memberId,
  );
  const canShowUserTasksNavBadge =
    isAdminPage &&
    (user?.isAdmin === true ||
      user?.isStaff === true ||
      user?.isTeacher === true);
  const { count: activeUserTasksCount } = useActiveUserTasksCount(
    canShowUserTasksNavBadge,
  );
  const userTasksNavBadge = useMemo((): UserTasksNavBadge | null => {
    if (!canShowUserTasksNavBadge || activeUserTasksCount <= 0) {
      return null;
    }
    return {
      count: activeUserTasksCount,
      tooltipSuffix: "active",
    };
  }, [activeUserTasksCount, canShowUserTasksNavBadge]);
  const makeupNavBadge = useMemo((): MakeupNavBadge | null => {
    if (canShowAdminMakeupBadge && pendingMakeupRequestsCount > 0) {
      return {
        count: pendingMakeupRequestsCount,
        tooltipSuffix: "pending",
      };
    }

    if (!isAdminPage && memberMakeupActionCount > 0) {
      return {
        count: memberMakeupActionCount,
        tooltipSuffix: "need your response",
      };
    }

    return null;
  }, [
    canShowAdminMakeupBadge,
    isAdminPage,
    memberMakeupActionCount,
    pendingMakeupRequestsCount,
  ]);
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId: tenantId ?? undefined },
    { enabled: !!tenantId },
  );

  const studioNavOptions = useMemo((): StudioNavOptions => ({
    features: {
      makeupsEnabled,
      appointmentsEnabled,
      timeClockEnabled,
      choreographerPortalEnabled: portalKnown ? choreographerPortalEnabled : false,
      contractorsEnabled: portalKnown ? contractorsEnabled : false,
      ticketsEnabled: portalKnown ? ticketsEnabled : false,
    },
    portalLabels: {
      students: contentSettings?.portalNavStudentsLabel,
      appointments: contentSettings?.portalNavAppointmentsLabel,
      makeups: contentSettings?.portalNavMakeupsLabel,
      terms: contentSettings?.portalNavTermsLabel,
    },
  }), [
    appointmentsEnabled,
    contentSettings?.portalNavAppointmentsLabel,
    contentSettings?.portalNavMakeupsLabel,
    contentSettings?.portalNavStudentsLabel,
    contentSettings?.portalNavTermsLabel,
    makeupsEnabled,
    timeClockEnabled,
    choreographerPortalEnabled,
    contractorsEnabled,
    ticketsEnabled,
    portalKnown,
  ]);

  const navGroups = useMemo(
    () => {
      if (isForbiddenPage) return [];
      if (isAdminPage) {
        if (visibleRole === UserRoles.Choreographer) {
          return getChoreographerLinkGroups(pathname ?? "", studioNavOptions);
        }
        if (visibleRole === UserRoles.Teacher) {
          return getTeacherLinkGroups(pathname ?? "", studioNavOptions);
        }
        if (visibleRole === UserRoles.Staff) {
          return getStaffLinkGroups(pathname ?? "", studioNavOptions);
        }
        return getAdminLinkGroups(pathname ?? "", studioNavOptions);
      }
      // For portal, return as single group without label
      const items = getPortalLinks(!!user?.isMember, pathname ?? "", studioNavOptions);
      return [{ items }];
    },
    [pathname, user?.isMember, isAdminPage, visibleRole, isForbiddenPage, studioNavOptions],
  );

  const useCollapsibleNav =
    isAdminPage &&
    !isCollapsed &&
    (visibleRole === UserRoles.Admin || visibleRole === UserRoles.Staff);

  const activeNavGroupIds = useMemo(
    () =>
      navGroups.flatMap((group) => {
        if (!group.collapsible || !group.id) return [];
        const currentPath = pathname ?? "";
        const containsCurrentPage = group.items.some(
          (item) =>
            item.active ||
            currentPath === item.href ||
            currentPath.startsWith(`${item.href}/`),
        );
        return containsCurrentPage ? [group.id] : [];
      }),
    [navGroups, pathname],
  );

  const { isExpanded, toggleGroup } =
    useSidebarGroupExpansion(activeNavGroupIds);

  const isProfileSettingsActive =
    pathname?.startsWith(PageTypePortal.ProfileSettings) ?? false;

  return (
    <Flex
      direction="column"
      {...fullViewportHeightProps}
      borderRightWidth= {isClassic ? "0px" : "1px"}
      borderColor={theme.borderGlass.default}
      bg={theme.surfaceGlass.sidebar}
      backdropFilter={backdropBlur ?? (isClassic ? "none" : "blur(14px)")}
      position="relative"
      _before={
        isClassic
          ? undefined
          : {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme.gradientEnabled
                ? theme.mode === "dark"
                  ? `linear-gradient(to right, ${hexToRgba(theme.accent.primary, 0.25)} 0%, ${hexToRgba(theme.accent.primary, 0.15)} 30%, ${hexToRgba(theme.accent.primary, 0.08)} 60%, transparent 100%)`
                  : `linear-gradient(to right, ${hexToRgba(theme.accent.primary, 0.14)} 0%, ${hexToRgba(theme.accent.primary, 0.08)} 30%, ${hexToRgba(theme.accent.primary, 0.04)} 60%, transparent 100%)`
                : "transparent",
              pointerEvents: "none",
              zIndex: 0,
            }
      }
    >
      <Box
        py={6}
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        position="relative"
        zIndex={1}
        overflowY="hidden"
        overflowX="hidden"
      >
        {/* Logo and Brand */}
        <Flex align="center" justify={isCollapsed ? "center" : "flex-start"} mb={6} position="relative">
          <Box
            flexShrink={0}
            transition="all 0.2s linear"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pl={isCollapsed ? 0 : 4}
          >
            <TenantLogo
              width={isCollapsed ? 38 : 48}
              height={isCollapsed ? 33 : 43}
              tenantData={tenantData}
              src={logoUrl ?? initialLogoUrl}
              alt={tenantName}
              priority
              style={{
                transition: "all 0.2s linear",
              }}
            />
          </Box>
          <Box
            minW="0"
            overflow="hidden"
            whiteSpace="nowrap"
            opacity={isCollapsed ? 0 : 1}
            w={isCollapsed ? 0 : "auto"}
            ml={isCollapsed ? 0 : 2}
            transition={
              isCollapsed
                ? "opacity 0s, width 0s, margin 0s"
                : "opacity 0.2s ease 0.2s, width 0.2s ease 0.2s, margin 0.2s ease 0.2s"
            }
          >
            <Text fontSize="md" fontWeight="500" color={theme.text.primary}>
              {tenantName}
            </Text>
          </Box>
        </Flex>

        {/* Navigation Links */}
        <VStack
          px={isCollapsed ? 2 : 4}
          align={isCollapsed ? "center" : "stretch"}
          spacing={2}
          flex="1"
          minH={0}
          overflowY="auto"
        >
          {navGroups.map((group, groupIndex) => {
            if (group.items.length === 0) return null;

            const groupKey = group.id ?? `group-${groupIndex}`;
            const isCollapsibleGroup =
              useCollapsibleNav && group.collapsible && group.id && group.label;

            return (
              <Box
                key={groupKey}
                w="100%"
                mt={groupIndex > 0 ? 1 : 0}
              >
                {isCollapsibleGroup ? (
                  <SidebarNavCollapsibleGroup
                    group={group}
                    isExpanded={isExpanded(group.id!)}
                    onToggle={() => toggleGroup(group.id!)}
                    isMobile={isMobile}
                    onNavigate={onClose}
                    controlBorderRadius={controlBorderRadius}
                    isClassic={isClassic}
                    theme={theme}
                    makeupNavBadge={makeupNavBadge}
                    userTasksNavBadge={userTasksNavBadge}
                  />
                ) : (
                  <VStack spacing={1} align="stretch">
                    {group.items.map((item) => (
                      <SidebarNavLinkItem
                        key={item.href}
                        item={item}
                        isCollapsed={isCollapsed}
                        isMobile={isMobile}
                        onNavigate={onClose}
                        controlBorderRadius={controlBorderRadius}
                        isClassic={isClassic}
                        theme={theme}
                        makeupNavBadge={makeupNavBadge}
                        userTasksNavBadge={userTasksNavBadge}
                      />
                    ))}
                  </VStack>
                )}

              </Box>
            );
          })}

          {/* Profile Settings (only for members, not on admin pages, not on forbidden) */}
          {user?.isMember && !isAdminPage && !isForbiddenPage && (
            <>
              {isCollapsed ? (
                <TouchAwareTooltip label="Profile Settings" placement="right">
                  <Link
                    href={PageTypePortal.ProfileSettings}
                    onClick={isMobile ? onClose : undefined}
                  >
                    <Flex
                      align="center"
                      justify="center"
                      px={2}
                      py={2.5}
                      w="40px"
                      borderRadius={controlBorderRadius}
                      gap={0}
                      bg={isProfileSettingsActive ? theme.accent.primaryBg : "transparent"}
                      color={isProfileSettingsActive ? theme.accent.primary : theme.text.secondary}
                      transition="all 0.18s ease-out"
                      sx={{
                        "@media (hover: hover)": {
                          "&:hover": {
                            bg:
                              theme.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(11,13,16,0.08)",
                            color: isProfileSettingsActive ? theme.accent.primary : theme.text.primary,
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                    >
                      <Icon as={HiOutlineUserCircle} boxSize={4} />
                    </Flex>
                  </Link>
                </TouchAwareTooltip>
              ) : (
                <Link
                  href={PageTypePortal.ProfileSettings}
                  onClick={isMobile ? onClose : undefined}
                >
                  <Flex
                    align="center"
                    justify="flex-start"
                    px={3}
                    py={2.5}
                    w="100%"
                    borderRadius={controlBorderRadius}
                    gap={3}
                    bg={isProfileSettingsActive ? theme.accent.primaryBg : "transparent"}
                    color={isProfileSettingsActive ? theme.accent.primary : theme.text.secondary}
                    transition="all 0.18s ease-out"
                    position="relative"
                    boxShadow={
                      isProfileSettingsActive
                        ? isClassic
                          ? "none"
                          : theme.mode === "dark"
                            ? "none"
                            : "0 0 0 rgba(255,255,255,0.65), 0 4px 12px rgba(11,13,16,0.15)"
                        : "none"
                    }
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          bg:
                            theme.mode === "dark"
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(11,13,16,0.08)",
                          color: isProfileSettingsActive ? theme.accent.primary : theme.text.primary,
                          transform: "translateX(2px)",
                        },
                      },
                    }}
                  >
                    <SidebarNavIcon as={HiOutlineUserCircle} />
                    <SidebarNavItemLabel compact>Profile Settings</SidebarNavItemLabel>
                  </Flex>
                </Link>
              )}
            </>
          )}

          <Box
            borderTopWidth="1px"
            borderTopColor={theme.borderGlass.default}
            w="100%"
          >
            {isMobile && (
              <Box pt={2}>
                <SidebarThemeToggle />
              </Box>
            )}
            <Box py={showStudioTimeZoneHint ? 2 : 0}>
              <StudioTimeZoneHint
                isCollapsed={isCollapsed}
                onVisibilityChange={setShowStudioTimeZoneHint}
                data-testid="sidebar-studio-timezone-hint"
              />
            </Box>
            <Box
              borderTopWidth={showStudioTimeZoneHint ? "1px" : undefined}
              borderTopColor={theme.borderGlass.default}
              pt={showStudioTimeZoneHint ? 2 : isMobile ? 2 : 0}
              w="100%"
              display="flex"
              justifyContent={isCollapsed ? "center" : "stretch"}
            >
              {isCollapsed ? (
                <TouchAwareTooltip label="Log out" placement="right">
                  <Flex
                    as="button"
                    data-testid="logout-button"
                    align="center"
                    justify="center"
                    px={2}
                    py={2.5}
                    w="40px"
                    borderRadius={controlBorderRadius}
                    gap={0}
                    {...getSidebarLogoutButtonStyles(theme, true)}
                    onClick={onLogoutModalOpen}
                  >
                    <SidebarNavIcon as={HiOutlineArrowRightOnRectangle} />
                  </Flex>
                </TouchAwareTooltip>
              ) : (
                <Flex
                  as="button"
                  data-testid="logout-button"
                  align="center"
                  justify="flex-start"
                  px={3}
                  py={2.5}
                  w="100%"
                  borderRadius={controlBorderRadius}
                  gap={3}
                  {...getSidebarLogoutButtonStyles(theme, false)}
                  onClick={onLogoutModalOpen}
                >
                  <SidebarNavIcon as={HiOutlineArrowRightOnRectangle} />
                  <SidebarNavItemLabel compact>
                    Log out
                  </SidebarNavItemLabel>
                </Flex>
              )}
            </Box>
          </Box>
        </VStack>

        {/* Collapse Button (only on desktop) */}
        <Box
          mt="auto"
          position="relative"
          h="40px"
          display={{ base: "none", md: "flex" }}
          alignItems="center"
          justifyContent={isCollapsed ? "center" : "flex-end"}
          mr={isCollapsed ? 0 : 4}
        >
          {!isMobile && onCollapse && (
            <TouchAwareTooltip
              label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement={isCollapsed ? "right" : "top"}
            >
              <IconButton
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                icon={<HiOutlineChevronLeft />}
                size="sm"
                variant="ghost"
                onClick={onCollapse}
                transform={isCollapsed ? "rotate(180deg)" : "none"}
                transition="transform 0.2s"
                color={theme.text.primary}
                bg={
                  theme.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(11,13,16,0.05)"
                }
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg:
                        theme.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(11,13,16,0.12)",
                      color: theme.text.primary,
                    },
                  },
                }}
              />
            </TouchAwareTooltip>
          )}
        </Box>
      </Box>
      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={onLogoutModalClose}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        confirmColorScheme="red"
        {...LOGOUT_CONFIRMATION_LOADING}
        data-testid="logout-dialog"
      />
      <LogoutTransitionOverlay
        isVisible={showLogoutOverlay}
        logoUrl={logoutLogoUrl}
        tenantData={logoutTenantData}
      />
    </Flex>
  );
};

